import { useCallback, useContext } from 'react';
import _groupBy from 'lodash/groupBy';
import _uniq from 'lodash/uniq';

import type { Sensor, Tenant } from 'services/sensor/SensorsProvider/types';
import { SensorsApiContext } from 'services/sensor/SensorsProvider';
import useStore from 'services/store';

type TenantWithSensors = Tenant & { sensors: Sensor[] };

const getTenantList = (sensorList: Sensor[]): TenantWithSensors[] => {
  if (!sensorList) {
    return [];
  }

  const groupedSensorList = _groupBy(sensorList, (s) => s.Tenant!.id);
  const tenants: TenantWithSensors[] = [];
  Object.values(groupedSensorList).forEach((sensorList) => {
    const tenant = sensorList[0].Tenant!;
    const tmpTenant: TenantWithSensors = { ...tenant, sensors: [] };
    tmpTenant.sensors = sensorList.map((sensor) => ({ ...sensor }));
    tenants.push(tmpTenant);
  });
  return tenants;
};

const useSensorList = () => {
  const sensorContext = useContext(SensorsApiContext);
  // TODO - zustand store is not typed yet
  const activeSensors: number[] = useStore(
    (state: { searchQuery: { activeSensors: number[] } }) => state.searchQuery.activeSensors,
  );
  const sensorList = sensorContext.state.sensors!;

  const sensorIds = sensorList ? sensorList.map((s) => s.id) : [];
  const tenantList = getTenantList(sensorList);

  const getActiveSensorsByProfileTypes = useCallback(
    (profileTypes: string[]) => {
      if (!sensorList) {
        return;
      }

      if (!profileTypes || profileTypes.length === 0) {
        return activeSensors.map((sid) => sensorList.find((s) => s.id === sid));
      }
      return sensorList.filter(
        (s) =>
          s?.Profile?.profileType && profileTypes.includes(s?.Profile?.profileType) && activeSensors.includes(s.id),
      );
    },
    [activeSensors, sensorList],
  );

  /** @function
   * takes array of profile types
   * @param {string[]} profileTypes
   * @returns {any[]}
   */
  const getSensorListByProfileTypes = (profileTypes: string[]) => {
    if (!sensorList) {
      return;
    }
    if (!profileTypes || profileTypes.length === 0) {
      return sensorList;
    }
    return sensorList.filter((s) => s?.Profile?.profileType && profileTypes.includes(s?.Profile?.profileType));
  };

  // is profile type in the list of currently active sensors
  const isProfileTypeActive = useCallback(
    (profileType: string) => {
      const activeSensorsByProfile = getActiveSensorsByProfileTypes([profileType]);
      return activeSensorsByProfile && activeSensorsByProfile.length > 0;
    },
    [getActiveSensorsByProfileTypes],
  );

  const areProfileTypesActive = useCallback(
    (profileTypes: string[]) => {
      const activeSensorsByProfile = getActiveSensorsByProfileTypes(profileTypes);
      return activeSensorsByProfile && activeSensorsByProfile.length > 0;
    },
    [getActiveSensorsByProfileTypes],
  );

  return {
    // array of sensors
    sensorList,
    sensorIds,
    // sensors grouped by tenant
    tenantList,
    // unique list of profile types across sensors
    profilesList: _uniq(sensorList?.map((s) => s.Profile?.profileType)),
    //
    findSensorById: (id: number) =>
      sensorList?.find((x) => parseInt(x.id as unknown as string, 10) === parseInt(id as unknown as string, 10)),
    // sadly we need to rely on label to know which data source sensor belongs to
    findSensorsWithLabel: (label: string) => sensorList?.filter((x) => x.labels && x.labels.includes(label)),
    getSensorProfileType: (id: number) => {
      return sensorContext.state.sensors?.find((x) => x.id === id)?.Profile?.profileType || null;
    },

    getTenantIdBySensorId: (sensorId: number) => {
      return sensorList?.find((x) => x.id === sensorId)?.Tenant?.id || null;
    },
    // active sensors
    activeSensors,
    getActiveSensorsByProfileTypes,
    isProfileTypeActive,
    areProfileTypesActive,
    getSensorListByProfileTypes,
  };
};

export default useSensorList;
